/**
 * Equipment panel (M3.3a C3) — 6-slot grid for player equipment.
 * Shows equipped item icon + quality badge in each slot.
 * Click on an equipped item emits an unequip intent.
 *
 * Modifier summary shows active bonuses from equipped items.
 * Displayed below the inventory panel in the same right-side column.
 */

import type { EquipmentSlot, PropStackWire } from '../wire';
import { propLabel } from '../data/prop-catalog';
import { useLiveWire } from '../wire';
import { useInventory } from './store';
import { QualityBadge } from './QualityBadge';
import styles from './inventory.module.css';

/** All 6 player equipment slots in display order. */
const PLAYER_SLOTS: EquipmentSlot[] = [
  'Tool',
  'Head',
  'Body',
  'Feet',
  'Accessory',
  'Bag',
];

/** Slot display labels. */
const SLOT_LABELS: Record<EquipmentSlot, string> = {
  Tool: 'Tool',
  Head: 'Head',
  Body: 'Body',
  Feet: 'Feet',
  Accessory: 'Accessory',
  Bag: 'Bag',
};

export function EquipmentPanel(): JSX.Element | null {
  const equipment = useInventory((s) => s.equipment);
  const send = useLiveWire((s) => s.send);

  if (!equipment) return null;

  const handleUnequip = (slot: EquipmentSlot): void => {
    const item = equipment.slots[slot];
    if (!item) return;
    // Emit unequip intent. The server decides the outcome.
    send({
      plane: 'World',
      intent: {
        Unequip: { slot },
      } as never,
    });
  };

  return (
    <div className={styles.section} data-testid="equipment-panel">
      <div className={styles.sectionTitle}>Equipment</div>
      <div className={styles.equipGrid}>
        {PLAYER_SLOTS.map((slot) => {
          const item = equipment.slots[slot] as PropStackWire | undefined;
          return (
            <div
              key={slot}
              className={styles.equipSlot}
              onClick={() => handleUnequip(slot)}
              role="button"
              aria-label={`${SLOT_LABELS[slot]} slot`}
              data-testid={`equip-slot-${slot}`}
            >
              <span className={styles.equipSlotLabel}>{SLOT_LABELS[slot]}</span>
              {item ? (
                <span className={styles.equipSlotItem}>
                  <QualityBadge quality={item.quality} />
                  {propLabel(item.prop_id)}
                </span>
              ) : (
                <span className={styles.equipSlotEmpty}>Empty</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
